import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { STAGES, api } from "@/sales/lib/api";
import { useAuth } from "@/sales/auth/AuthContext";
import { Trash2, Activity, FileText } from "lucide-react";
import {
  OFFER_TEMPLATE,
  dealToOfferFields,
  stashOfferPrefill,
} from "@/lib/offerPrefill";

const empty = {
  municipality: "",
  value: "",
  stage: "lead",
  last_action: "",
  next_action: "",
  owner: "",
  eigenaar: "",
  next_action_date: "",
  notes: "",
  outcome: null,
};

export default function DealDialog({ open, onOpenChange, deal, onSave, onDelete }) {
  const [form, setForm] = useState(empty);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const { user, isBestuurslid } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (open && isBestuurslid) {
      api
        .get("/auth/users")
        .then((r) => {
          // Eigenaar must be an active deal owner — exclude viewers.
          const filtered = (r.data || []).filter((u) => u.role !== "gast");
          setAssignableUsers(filtered);
        })
        .catch(() => setAssignableUsers([]));
    }
  }, [open, isBestuurslid]);

  useEffect(() => {
    if (open) {
      if (deal) {
        setForm({
          municipality: deal.municipality || "",
          value: deal.value ?? "",
          stage: deal.stage || "lead",
          last_action: deal.last_action || "",
          next_action: deal.next_action || "",
          owner: deal.owner || "",
          eigenaar: deal.eigenaar || "",
          next_action_date: deal.next_action_date || "",
          notes: deal.notes || "",
          outcome: deal.outcome || null,
        });
      } else {
        setForm({ ...empty, eigenaar: user?.username || "" });
      }
    }
  }, [open, deal, user]);

  const update = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // Auto-default outcome when stage becomes 'gewonnen'
      if (k === "stage" && v === "gewonnen" && !f.outcome) next.outcome = "gewonnen";
      if (k === "stage" && v !== "gewonnen") next.outcome = null;
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = (form.municipality || "").trim();
    if (!name) return;

    // Robust value coercion: strip € / commas / whitespace, accept decimals
    let rawValue = form.value;
    if (typeof rawValue === "string") {
      rawValue = rawValue.replace(/[€\s,]/g, "");
    }
    const numericValue = parseFloat(rawValue);
    const finalValue = Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;

    const payload = {
      municipality: name,
      value: finalValue,
      stage: form.stage,
      last_action: form.last_action || "",
      next_action: form.next_action || "",
      owner: form.owner || "",
      eigenaar: form.eigenaar || "",
      next_action_date: form.next_action_date || null,
      notes: form.notes || "",
      outcome: form.stage === "gewonnen" ? (form.outcome || "gewonnen") : null,
    };
    onSave(payload);
  };

  // Hand the deal off to the internal offer generator: template id via URL,
  // field values via localStorage (see @/lib/offerPrefill). Opens in a new
  // tab so the pipeline stays put; /internal/* is a separate Caddy Basic-Auth
  // surface, so this is a fresh top-level context either way.
  const handleGenerateOffer = () => {
    stashOfferPrefill({ template: OFFER_TEMPLATE, fields: dealToOfferFields(deal) });
    window.open(
      `/internal/documents?template=${OFFER_TEMPLATE}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#142539] border-[rgba(192,192,192,0.2)] text-[#F8FAFC] max-w-xl"
        data-testid="deal-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl font-medium text-[#F8FAFC]">
            {deal ? "Deal Bewerken" : "Nieuwe Organisatie"}
          </DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            {deal
              ? "Werk dealgegevens en volgende actie bij."
              : "Voeg een nieuwe organisatie toe aan de pipeline."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Organisatie" required>
              <Input
                data-testid="input-municipality"
                value={form.municipality}
                onChange={(e) => update("municipality", e.target.value)}
                placeholder="Provincie Friesland, Gemeente Amsterdam …"
                className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
                required
              />
            </Field>
            <Field label="Deal Waarde (€)" required>
              <Input
                data-testid="input-value"
                type="number"
                min="0"
                step="1000"
                value={form.value}
                onChange={(e) => update("value", e.target.value)}
                placeholder="150000"
                className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
                required
              />
            </Field>
          </div>

          <Field label="Fase">
            <Select
              value={form.stage}
              onValueChange={(v) => update("stage", v)}
            >
              <SelectTrigger
                data-testid="select-stage"
                className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#142539] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]">
                {STAGES.map((s) => (
                  <SelectItem key={s.id} value={s.id} className="focus:bg-[#1C3A5A]">
                    {s.number}. {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {form.stage === "gewonnen" && (
            <Field label="Uitkomst">
              <Select
                value={form.outcome || "gewonnen"}
                onValueChange={(v) => update("outcome", v)}
              >
                <SelectTrigger
                  data-testid="select-outcome"
                  className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#142539] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]">
                  <SelectItem value="gewonnen" className="focus:bg-[#1C3A5A]">Gewonnen</SelectItem>
                  <SelectItem value="verloren" className="focus:bg-[#1C3A5A]">Verloren</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}

          <Field label="Laatste Actie">
            <Input
              data-testid="input-last-action"
              value={form.last_action}
              onChange={(e) => update("last_action", e.target.value)}
              placeholder="Demo afgerond"
              className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
            />
          </Field>

          <Field label="Volgende Actie">
            <Textarea
              data-testid="input-next-action"
              value={form.next_action}
              onChange={(e) => update("next_action", e.target.value)}
              placeholder="Vervolggesprek inplannen met inkoop"
              rows={2}
              className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC] resize-none"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Eigenaar">
              <Input
                data-testid="input-owner"
                value={form.owner}
                onChange={(e) => update("owner", e.target.value)}
                placeholder="Sander van Dijk"
                className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
              />
            </Field>
            <Field label="Datum Volgende Actie">
              <Input
                data-testid="input-next-action-date"
                type="date"
                value={form.next_action_date}
                onChange={(e) => update("next_action_date", e.target.value)}
                className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
              />
            </Field>
          </div>

          <Field label={isBestuurslid ? "Toegewezen accountmanager" : "Toegewezen aan"}>
            {isBestuurslid ? (
              <Select
                value={form.eigenaar || ""}
                onValueChange={(v) => update("eigenaar", v)}
              >
                <SelectTrigger
                  data-testid="select-eigenaar"
                  className="bg-[#0B1521] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]"
                >
                  <SelectValue placeholder="Selecteer eigenaar…" />
                </SelectTrigger>
                <SelectContent className="bg-[#142539] border-[rgba(192,192,192,0.25)] text-[#F8FAFC]">
                  {assignableUsers.map((u) => (
                    <SelectItem
                      key={u.username}
                      value={u.username}
                      className="focus:bg-[#1C3A5A]"
                    >
                      {u.display_name} · {u.role === "bestuurslid" ? "Super-Admin" : "Accountmanager"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                data-testid="input-eigenaar-readonly"
                value={form.eigenaar || user?.username || ""}
                disabled
                className="bg-[#0B1521]/60 border-[rgba(192,192,192,0.15)] text-[#94A3B8] cursor-not-allowed"
              />
            )}
          </Field>

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            {deal && (
              <>
                <Button
                  data-testid="delete-deal-btn"
                  type="button"
                  variant="ghost"
                  onClick={() => onDelete(deal.id)}
                  className="text-rose-400 hover:text-rose-300 hover:bg-rose-900/20"
                >
                  <Trash2 size={14} className="mr-1" /> Verwijderen
                </Button>
                <Button
                  data-testid="dialog-voortgang-btn"
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(`/sales/traject/${deal.id}/voortgang`);
                  }}
                  className="text-[#C0C0C0] hover:text-white hover:bg-[#1C3A5A]"
                >
                  <Activity size={14} className="mr-1" /> Voortgang
                </Button>
                <Button
                  data-testid="dialog-genereer-offerte-btn"
                  type="button"
                  variant="ghost"
                  onClick={handleGenerateOffer}
                  className="mr-auto text-[#C0C0C0] hover:text-white hover:bg-[#1C3A5A]"
                >
                  <FileText size={14} className="mr-1" /> Genereer offerte
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[rgba(192,192,192,0.3)] bg-transparent text-[#E2E8F0] hover:bg-[#1C3A5A] hover:text-white"
            >
              Annuleren
            </Button>
            <Button
              data-testid="save-deal-btn"
              type="submit"
              className="bg-[#C0C0C0] hover:bg-white text-[#0B1521] font-medium"
            >
              {deal ? "Bijwerken" : "Toevoegen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">
        {label} {required && <span className="text-rose-400">*</span>}
      </Label>
      {children}
    </div>
  );
}
